import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MuiTableCell from '@material-ui/core/TableCell';
import styled from 'src/ui/library/styled';
import Checkbox from 'src/ui/library/Checkbox';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ListIcon from '@material-ui/icons/List';
import Popper from '@material-ui/core/Popper';
import Paper from '@material-ui/core/Paper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import intl from 'react-intl-universal';
import DeleteIcon from '@material-ui/icons/Delete';
import _ from 'lodash';
//import { Delete } from '@material-ui/icons';

const styles = theme => ({
  root: {
    height: '45px',
    border: 'none',
    width: '70px',
    maxWidth: '70px',
    minWidth: '70px',
    padding: '0px',
    fontSize: '12px',
    backgroundColor: '#FFF',
    position: 'sticky',
    left: '0px',
    zIndex: 80,
    borderBottom: `solid 1px ${theme.colors.eyGrey3}`,
    //padding: '4px 8px 4px 24px',
    '& .content-parser-row': {
      marginBottom: '0px',
    },
    // background & background-clip are used to force a reflow since EDGE/IE have issues with sticky positions
    // https://stackoverflow.com/questions/41882616/why-border-is-not-visible-with-position-sticky-when-background-color-exists
    background: '#FFF',
    backgroundClip: 'padding-box',
  },
  noSelectable: {
    width: '50px',
    maxWidth: '50px',
    minWidth: '50px',
    '& $actionContainer': {
      padding: '0px',
      justifyContent: 'center',
    },
  },
  lastRow: {
    borderBottom: 'none',
  },
  actionContainer: {
    display: 'flex',
    width: '100%',
    height: '100%',
    borderRight: 'solid 1px #EAEAF2',
    paddingLeft: '5px',
    paddingRight: '5px',
    position: 'relative',
  },
  checkboxContainer: {
    width: '100%',
    height: '100%',
    cursor: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  moreButton: {
    border: 'none',
    background: 'none',
    margin: 0,
    padding: 0,
    position: 'relative',
    top: '2px',
    cursor: 'pointer',
  },
  vertIcon: {
    fill: '#595959',
  },
  paper: {
    maxWidth: 400,
    overflow: 'auto',
  },
  popper: {
    zIndex: 9999,
    left: '3px !important',
    '&[x-placement*="right"] $arrow': {
      left: 0,
      marginLeft: '-0.7em',
      height: '3em',
      width: '3em',
      '&::before': {
        borderRightColor: '#EAEAF2',
        borderWidth: '9px',
        marginLeft: '-13px',
        zIndex: 500,
      },
      '&::after': {
        borderRightColor: '#FFF',
        borderWidth: '8px',
        top: '-17px',
        left: '-13px',
        zIndex: 1000,
      },
    },
  },
  arrow: {
    position: 'absolute',
    fontSize: 7,
    width: '3em',
    height: '3em',
    '&::before': {
      content: '""',
      margin: 'auto',
      display: 'block',
      width: 0,
      height: 0,
      border: 'solid transparent',
      position: 'relative',
    },
    '&::after': {
      content: '""',
      margin: 'auto',
      display: 'block',
      width: 0,
      height: 0,
      border: 'solid transparent',
      position: 'relative',
    },
  },
  actionMenu: {
    minWidth: '180px',
    '& > ul': {
      padding: 0,
      margin: 0,
      listStyleType: 'none',
      '& > li': {
        padding: '8px',
        '&:hover': {
          backgroundColor: '#F6F6FA',
        },
        '& > a': {
          paddingLeft: '8px',
          display: 'flex',
          alignItems: 'center',
          textDecoration: 'none',
          color: '#2E2E38',
          cursor: 'pointer',
          '& > span': {
            paddingLeft: '6px',
          },
        },
      },
    },
  },
});

class TableActionBodyCell extends React.Component {
  static propTypes = {
    /** Row index value */
    rowIndex: PropTypes.number,
    /** Column index value */
    colIndex: PropTypes.number,
    /** Callback function for to handle row selection */
    onRowSelect: PropTypes.func.isRequired,
    /** Callback reference to the internal setState */
    setUpdateSelectCell: PropTypes.func.isRequired,
    /** Styles to be applied to component */
    classes: PropTypes.object,
    showCheckbox: PropTypes.bool,
    notApplicable: PropTypes.bool,
    notSure: PropTypes.bool,
    onDelete: PropTypes.func,
  };

  state = {
    isMenuOpen: false,
    arrowRef: null,
  };

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
    this.props.tableRef.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    this.props.tableRef.removeEventListener('scroll', this.handleScroll);
  }

  handleClick = () => {
    const { rowIndex, rowData, onRowSelect } = this.props;
    onRowSelect({ origRowIndex: rowData[0].origRowIndex, rowIndex });
  };

  handleClickAway = event => {
    if (!this.anchorEl.contains(event.target) && !this.popupContentEl.contains(event.target)) {
      this.handleMenuClick(false);
    }
  };

  updateCellRef = () => {
    this.setState({ updateCell: true });
  };

  handleMenuClick = status => {
    this.setState(
      prevState => ({
        isMenuOpen: status !== undefined ? status : !prevState.isMenuOpen,
      }),
      () => {
        setTimeout(() => {
          if (this.state.isMenuOpen) {
            this.popupContentEl.focus();
          } else {
            if (this.anchorEl) {
              this.anchorEl.focus();
            }
          }
        }, 250);
      },
    );
  };

  handleScroll = () => {
    if (this.state.isMenuOpen) {
      this.setState({ isMenuOpen: false }, () => {
        this.anchorEl.focus();
      });
    }
  };

  handleKeyDown = event => {
    if (event.keyCode === 27 && this.state.isMenuOpen) {
      this.setState({ isMenuOpen: false }, () => {
        this.anchorEl.focus();
      });
    }
  };

  handleArrowRef = node => {
    this.setState({
      arrowRef: node,
    });
  };

  render() {
    const {
      classes,
      rowData,
      isLastRow,
      rowIndex,
      selectableRows,
      actionable,
      actionableRender,
      setUpdateSelectCell,
      onViewDetail,
      getMetaForMenu,
      showCheckbox,
      notApplicable,
      notSure,
      isTaskSubmitted,
    } = this.props;
    const isSubmitted = this.props.pageData ? this.props.pageData.isSubmitted : false;

    setUpdateSelectCell(rowIndex, this.updateCellRef);

    const rootClass = classNames({
      [classes.root]: true,
      [classes.lastRow]: isLastRow,
      'action-cell': true,
      [classes.noSelectable]: !selectableRows,
    });

    return (
      <MuiTableCell className={rootClass} ref={ref => (this.cellRef = ref)}>
        <div className={classNames(classes.actionContainer, 'action-cell-container')}>
          {selectableRows && (
            <div className={classes.checkboxContainer}>
              <Checkbox
                disabled={notApplicable || notSure || isSubmitted || isTaskSubmitted}
                //defaultChecked={rowData[0].isRowSelected}
                checked={!notApplicable && !notSure && _.nth(rowData, 0).isRowSelected}
                key={notApplicable || notSure || _.nth(rowData, 0).isRowSelected}
                label={null}
                onChange={this.handleClick}
              />
              {/* <div>
              <a className="link" onClick={onDelete}>
                <Delete className="icon" /> <span className="text">{intl.get(`${'profile'}.details.buttons.delete`)}</span>
              </a></div> */}
            </div>
          )}
          {actionable && (
            <React.Fragment>
              {showCheckbox && !isSubmitted && (
                <button
                  ref={ref => (this.anchorEl = ref)}
                  className={classes.moreButton}
                  aria-label="More Options"
                  aria-haspopup="true"
                  disabled={isTaskSubmitted}
                  aria-expanded={this.state.isMenuOpen}
                  onClick={() => this.handleMenuClick()}>
                  <DeleteIcon />
                  {/* <span className="action-item-text">{intl.get('table.labels.delete')}</span> */}
                </button>
              )}

              {!showCheckbox && (
                <button
                  ref={ref => (this.anchorEl = ref)}
                  className={classes.moreButton}
                  aria-label="More Options"
                  aria-haspopup="true"
                  aria-expanded={this.state.isMenuOpen}
                  onClick={() => this.handleMenuClick()}>
                  <MoreVertIcon className={classes.vertIcon} />
                </button>
              )}

              <Popper
                open={this.state.isMenuOpen}
                anchorEl={this.anchorEl}
                placement="right"
                disablePortal={false}
                className={classes.popper}
                modifiers={{
                  flip: {
                    enabled: false,
                  },
                  preventOverflow: {
                    enabled: true,
                    boundariesElement: 'scrollParent',
                  },
                  hide: {
                    enabled: false,
                  },
                  arrow: {
                    enabled: true,
                    element: this.state.arrowRef,
                  },
                }}>
                <ClickAwayListener onClickAway={this.handleClickAway}>
                  <div>
                    <span className={classes.arrow} ref={this.handleArrowRef} />
                    <Paper className={classes.paper}>
                      <div className={classNames(classes.actionMenu, 'action-menu')} tabIndex={0} ref={ref => (this.popupContentEl = ref)}>
                        <ul className="action-menu-list">
                          {actionableRender ? (
                            actionableRender(
                              getMetaForMenu({ rowClicked: { rowIndex, origRowIndex: rowData[0].origRowIndex } }),
                              this.handleMenuClick,
                            )
                          ) : (
                            <li>
                              <a
                                aria-label="View Details for Row"
                                tabIndex={0}
                                onClick={event => {
                                  onViewDetail({ rowIndex, origRowIndex: rowData[0].origRowIndex }, event);
                                  this.handleMenuClick(false);
                                }}>
                                <ListIcon />
                                <span className="action-item-text">{intl.get('table.labels.viewDetails')}</span>
                              </a>
                            </li>
                          )}
                        </ul>
                      </div>
                    </Paper>
                  </div>
                </ClickAwayListener>
              </Popper>
            </React.Fragment>
          )}
        </div>
      </MuiTableCell>
    );
  }
}

export default styled(TableActionBodyCell)(styles);
